/** Opportunity creation wizard — numeric values match persisted/API step indices. */
export enum OpportunityWizardStep {
  Step1UploadStart = 1,
  Step2SmartDiscovery = 2,
  Step3Scotsman = 3,
  Step4OpportunityMetadata = 4,
  Step5OpportunityAssessment = 5,
}

export type DocumentFileType = 'PDF' | 'DOCX' | 'DOC' | 'CSV' | 'XLSX';

/** AppApi `WorkspaceFileProcessingStatus` JSON (camelCase). */
export type WorkspaceFileProcessingStatusValue =
  'pending' | 'processed' | 'failed' | 'chunked' | 'indexed';

/** SharePoint workspace folder the file was listed from (set when mapping AppApi lists). */
export type WorkspaceDocumentScope = 'customer' | 'internal';

export interface UploadedDocument {
  name: string;
  fileType: DocumentFileType;
  sizeBytes: number;
  pages?: number;
  /** Local blob preview before upload, or SharePoint web URL after upload. */
  url?: string;
  /** Workspace file id when listed from the API (no local blob URL). */
  serverFileId?: string;
  /** AppApi `uploadedAt` (ISO 8601). */
  uploadedAt?: string;
  /** Ingestion package for this upload batch (from AppApi). */
  opportunityIngestionPackageId?: string;
  correlationId?: string;
  /** AppApi `processingStatus` (camelCase enum string). */
  processingStatus?: WorkspaceFileProcessingStatusValue;
  lastProcessingError?: string | null;
  indexStatus?: string | null;
  workspaceDocumentScope?: WorkspaceDocumentScope;
}

export function isInternalWorkspaceDocument(document: UploadedDocument): boolean {
  return document.workspaceDocumentScope === 'internal';
}

/** Per-file delete UI on step 1 (keys are {@link UploadedDocument.serverFileId}). */
export type PersistedWorkspaceFileDeleteUiState = Record<
  string,
  { loading: boolean; error: string | null }
>;

/** Maps AppApi workspace file DTO to sidebar cards (camelCase JSON). */
export function uploadedDocumentFromWorkspaceFileDto(f: unknown): UploadedDocument {
  const raw =
    typeof f === 'object' && f !== null
      ? (f as Record<string, unknown>)
      : ({} as Record<string, unknown>);
  const fileName = String(raw['fileName'] ?? '');
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const typeMap: Record<string, DocumentFileType> = {
    pdf: 'PDF',
    docx: 'DOCX',
    doc: 'DOC',
    csv: 'CSV',
    xlsx: 'XLSX',
    xls: 'XLSX',
  };
  const id = String(raw['id'] ?? '');
  const sizeInBytes = raw['sizeInBytes'] as number | null | undefined;
  const processingRaw = raw['processingStatus'];
  const processingStatus =
    typeof processingRaw === 'string'
      ? (processingRaw.trim().toLowerCase() as WorkspaceFileProcessingStatusValue)
      : undefined;
  const lastRaw = raw['lastProcessingError'];
  const lastProcessingError =
    typeof lastRaw === 'string' ? lastRaw : lastRaw === null ? null : undefined;
  const indexRaw = raw['indexStatus'];
  const indexStatus =
    typeof indexRaw === 'string' ? indexRaw : indexRaw === null ? null : undefined;
  const pkgRaw = raw['opportunityIngestionPackageId'];
  const opportunityIngestionPackageId = typeof pkgRaw === 'string' && pkgRaw ? pkgRaw : undefined;
  const corrRaw = raw['correlationId'];
  const correlationId = typeof corrRaw === 'string' && corrRaw ? corrRaw : undefined;
  const sharePointWebUrlRaw = raw['sharePointWebUrl'];
  const sharePointWebUrl =
    typeof sharePointWebUrlRaw === 'string' && sharePointWebUrlRaw
      ? sharePointWebUrlRaw
      : undefined;
  const uploadedAtRaw = raw['uploadedAt'];
  const uploadedAt =
    typeof uploadedAtRaw === 'string' && uploadedAtRaw.trim() ? uploadedAtRaw.trim() : undefined;
  return {
    name: fileName,
    fileType: typeMap[ext] ?? 'PDF',
    sizeBytes: sizeInBytes ?? 0,
    serverFileId: id || undefined,
    opportunityIngestionPackageId,
    correlationId,
    processingStatus:
      processingStatus === 'pending' ||
      processingStatus === 'processed' ||
      processingStatus === 'failed' ||
      processingStatus === 'chunked' ||
      processingStatus === 'indexed'
        ? processingStatus
        : undefined,
    lastProcessingError,
    indexStatus,
    url: sharePointWebUrl,
    uploadedAt,
  };
}
